import { Check, ChevronDown } from 'lucide-react';
import { Select as Primitive } from 'radix-ui';
import type { ComponentProps } from 'react';
import { cn } from '../../lib/utils.ts';

/**
 * shadcn/ui Select on Radix, in the design system's look (TS-1681): the trigger is the design
 * system's base select trigger, the list is its v2 Dropdown (12px panel, 40px rows with
 * dividers, light-purple focus and selection, purple check), from the copied tokens. The
 * behaviour stays Radix's: the design system's own Select is a custom floating-ui component,
 * and the CRM's pages and tests are written against Radix's.
 */
export const Select = Primitive.Root;
export const SelectValue = Primitive.Value;

/** The trigger's look, for a control that opens like a select but is not one (a multi-pick). */
export const selectTriggerClass =
  'border-input data-[placeholder]:text-muted-foreground hover:bg-accent/50 dark:hover:bg-input/50 focus-visible:border-ring focus-visible:ring-ring/50 dark:bg-input/30 flex h-9 w-full items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow,background-color] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 [&>span]:truncate';

export function SelectTrigger({
  className,
  children,
  ...props
}: ComponentProps<typeof Primitive.Trigger>) {
  return (
    <Primitive.Trigger className={cn(selectTriggerClass, className)} {...props}>
      {children}
      <Primitive.Icon asChild>
        <ChevronDown className="text-muted-foreground size-4" />
      </Primitive.Icon>
    </Primitive.Trigger>
  );
}

export function SelectContent({
  className,
  children,
  position = 'popper',
  ...props
}: ComponentProps<typeof Primitive.Content>) {
  return (
    <Primitive.Portal>
      <Primitive.Content
        position={position}
        className={cn(
          'ds-v2 animate-pop-in relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-[12px] border border-(--v2-dropdown-border) bg-(--v2-dropdown-bg) text-(--v2-dropdown-text) shadow-(--v2-dropdown-shadow)',
          position === 'popper' && 'w-full min-w-[var(--radix-select-trigger-width)]',
          className,
        )}
        {...props}
      >
        <Primitive.Viewport>{children}</Primitive.Viewport>
      </Primitive.Content>
    </Primitive.Portal>
  );
}

export function SelectItem({
  className,
  children,
  ...props
}: ComponentProps<typeof Primitive.Item>) {
  return (
    <Primitive.Item
      className={cn(
        'relative flex h-10 w-full cursor-default items-center border-b border-(--v2-dropdown-border) pr-3 pl-8 text-sm outline-none select-none last:border-b-0 hover:bg-(--v2-dropdown-bg-hover) focus:bg-(--v2-dropdown-bg-active) data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[state=checked]:bg-(--v2-dropdown-bg-active)',
        className,
      )}
      {...props}
    >
      <span className="absolute left-2 flex size-4 items-center justify-center">
        <Primitive.ItemIndicator>
          <Check className="size-4 text-(--v2-dropdown-check)" />
        </Primitive.ItemIndicator>
      </span>
      <Primitive.ItemText>{children}</Primitive.ItemText>
    </Primitive.Item>
  );
}
